<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick, defineAsyncComponent } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { InteractiveTarget } from "@/components/3DGarden.vue";
import GardenOverlay from "@/components/GardenOverlay.vue";
import GardenHint from "@/components/GardenHint.vue";
import MusicButton from "@/components/MusicButton.vue";
import { useAudio } from "@/composables/useAudio";

const Garden3D = defineAsyncComponent(() => import("@/components/3DGarden.vue"));

const INTRO_FLIGHT_PROGRESS = 0.05;
const INTRO_FLIGHT_DURATION = 2.6;

const route = useRoute();
const router = useRouter();
const { isMusicPlaying, playMusic, toggleMusic } = useAudio();

type Phase = "loading" | "ready" | "entering" | "exploring";
const phase = ref<Phase>("loading");
const progress = ref<number | null>(null);
const isFinished = ref(false);
const isUnloading = ref(false);
const hasSeenIntro = ref(false);

const hintText = ref<string | null>(null);
const gardenRef = ref<{
  resetScene: () => void;
  beginIntroFlight: (progress: number, duration: number, onComplete?: () => void) => void;
} | null>(null);

const isSubRouteActive = computed(() => route.path !== "/");
const isCoarse = window.matchMedia("(pointer: coarse)").matches;

let savedScrollY = 0;
let hintIndex = -1;
let hintTimeout: number | undefined;
let minLoadingTimer: number | undefined;
let userScrollBound = false;
let modelReady = false;
let minTimePassed = false;
const doneActions = { scroll: false, drag: false };

const HINTS = isCoarse
  ? ["swipe to move", "drag to look around", "tap the signs"]
  : ["scroll to move", "drag to look around", "click on the signs"];

watch(() => route.path, (newPath, oldPath) => {
  if (oldPath === undefined) return;
  if (newPath !== "/") {
    if (oldPath === "/") savedScrollY = window.scrollY;
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";

    if (isFinished.value) {
      isFinished.value = false;
      savedScrollY = 0;
      gardenRef.value?.resetScene();
    } else {
      nextTick(() => {
        window.scrollTo({ top: savedScrollY, behavior: "instant" });
      });
    }
  }
});

const interactiveTargets: InteractiveTarget[] = [
  { id: "teapot", position: { x: 7.2, y: 0.1, z: -4.5 }, title: "About me" },
  { id: "projects", position: { x: -13, y: 1.5, z: -33.0 }, title: "Things I built" },
  { id: "games", position: { x: -22.0, y: 0.2, z: -14.0 }, title: "Mini-games" },
];

const showNextHint = () => {
  window.clearTimeout(hintTimeout);
  hintIndex += 1;
  while (hintIndex < HINTS.length) {
    if (hintIndex === 0 && doneActions.scroll) { hintIndex += 1; continue; }
    if (hintIndex === 1 && doneActions.drag) { hintIndex += 1; continue; }
    break;
  }
  if (hintIndex >= HINTS.length) {
    hintText.value = null;
    return;
  }
  hintText.value = HINTS[hintIndex];
  hintTimeout = window.setTimeout(showNextHint, 7000);
};

const dismissHint = (index: number) => {
  if (index === 0) doneActions.scroll = true;
  if (index === 1) doneActions.drag = true;
  if (hintIndex === index) showNextHint();
};

const onFirstUserScroll = () => {
  window.removeEventListener("scroll", onFirstUserScroll);
  userScrollBound = false;
  dismissHint(0);
};

const tryEnterReady = () => {
  if (modelReady && minTimePassed && phase.value === "loading") {
    progress.value = 1;
    phase.value = "ready";
  }
};

const handleModelLoaded = () => {
  modelReady = true;
  tryEnterReady();
};

const beginUserHints = () => {
  phase.value = "exploring";
  if (hasSeenIntro.value) return;
  hasSeenIntro.value = true;
  hintIndex = -1;
  showNextHint();
  if (!userScrollBound) {
    userScrollBound = true;
    window.addEventListener("scroll", onFirstUserScroll, { passive: true });
  }
};

const enterGarden = () => {
  if (phase.value !== "ready") return;
  phase.value = "entering";
  playMusic();

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  gardenRef.value?.beginIntroFlight(
    INTRO_FLIGHT_PROGRESS,
    reducedMotion ? 0.01 : INTRO_FLIGHT_DURATION,
    beginUserHints
  );
};

onMounted(() => {
  if (route.path !== "/") {
    document.body.style.overflow = "hidden";
  }
  minLoadingTimer = window.setTimeout(() => {
    minTimePassed = true;
    tryEnterReady();
  }, 1000);
});

const handleSceneUnload = () => {
  if (isUnloading.value) return;
  isUnloading.value = true;
  window.clearTimeout(hintTimeout);
  hintText.value = null;
  setTimeout(() => {
    isFinished.value = true;
    isUnloading.value = false;
    router.push("/contact");
  }, 1000);
};

const handleIndicatorClicked = (target: InteractiveTarget) => {
  dismissHint(2);
  if (target.id === "teapot") router.push("/about");
  else if (target.id === "projects") router.push("/projects");
  else if (target.id === "games") router.push("/games");
};

const closeOverlay = () => {
  router.push("/");
};

onUnmounted(() => {
  window.clearTimeout(hintTimeout);
  window.clearTimeout(minLoadingTimer);
  if (userScrollBound) window.removeEventListener("scroll", onFirstUserScroll);
});
</script>

<template>
  <section id="projects" class="bg-pink-dark relative w-full min-h-screen overflow-x-hidden">

    <Transition
      enter-active-class="transition-opacity duration-700 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-[900ms] ease-in"
      leave-to-class="opacity-0"
    >
      <GardenOverlay
        v-if="phase === 'loading' || phase === 'ready'"
        :phase="phase === 'loading' ? 'loading' : 'ready'"
        :progress="progress"
        @enter="enterGarden"
      />
    </Transition>

    <Transition
      appear
      enter-active-class="transition-opacity duration-[1200ms] ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
    >
      <Garden3D
        ref="gardenRef"
        :targets="interactiveTargets"
        :paused="phase === 'loading' || isSubRouteActive"
        @modelLoaded="handleModelLoaded"
        @loadProgress="progress = $event"
        @firstDrag="dismissHint(1)"
        @sceneUnload="handleSceneUnload"
        @indicatorClicked="handleIndicatorClicked"
      />
    </Transition>

    <GardenHint :text="hintText" />

    <Transition
      enter-active-class="transition-opacity duration-[450ms] ease-out"
      leave-active-class="transition-opacity duration-[350ms] ease-in"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isSubRouteActive"
        class="fixed inset-0 z-[999999]"
        :class="{ 'overflow-y-auto bg-[#e4cbce]': route.path === '/games' }"
      >
        <router-view v-slot="{ Component }">
          <component :is="Component" @back="closeOverlay" />
        </router-view>
      </div>
    </Transition>

    <Transition
      enter-active-class="transition-opacity duration-1000 ease-in-out"
      leave-active-class="transition-opacity duration-1000 ease-in-out"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div v-if="isUnloading" class="fixed inset-0 z-40 bg-white"></div>
    </Transition>

    <MusicButton
      v-if="phase === 'exploring' || isSubRouteActive"
      :is-playing="isMusicPlaying"
      @toggle="toggleMusic"
    />

  </section>
</template>